import { Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Breadcrumb } from '@/components/common/Breadcrumb';
import { PageHeader } from '@/components/common/PageHeader';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import { Can } from '@/components/permission/Can';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { useToast } from '@/components/shared/Toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import {
  useAttachMemoryMutation,
  useAttachPoliciesMutation,
  useCreateTriggerMutation,
  useDeleteTriggerMutation,
  useDetachMemoryMutation,
  useGetFactoryQuery,
  useGetModelCollectionsQuery,
  useGetTriggersQuery,
  useRevokePolicyMutation,
  useUpdateFactoryMutation,
} from '../api/agentsApi';
import { GrantPanel } from '../components/GrantPanel';
import { MonoChip } from '../components/MonoChip';
import { PolicyStatementBuilder } from '../components/PolicyStatementBuilder';
import { SubsetDeniedAlert } from '../components/SubsetDeniedAlert';
import { TriggerBuilder } from '../components/TriggerBuilder';
import type {
  CreateFactoryTriggerBody,
  GrantStatementDraft,
  StepErrors,
  TriggerDraft,
} from '../types';
import {
  agentErrorMessage,
  memoryCollectionIds,
  parseSubsetDenial,
  toExtraGrantsSpec,
} from '../utils';
import { WebhookRevealPanel } from '../wizard/WebhookRevealPanel';

const UPDATE_ACTION = 'agent-factory:factories:update';

function triggerBody(item: TriggerDraft): CreateFactoryTriggerBody {
  if (item.kind === 'cron') {
    return { kind: 'cron', cron: item.cron };
  }
  if (item.kind === 'event') {
    return {
      kind: 'event',
      eventFilter: [{ channel: item.channel ?? '', eventTypes: item.eventTypes ?? [] }],
    };
  }
  return { kind: 'webhook' };
}

/** Inline error block: subset denial renders pairs, anything else verbatim. */
function AttachError({ error, title }: { error: unknown; title: string }) {
  const denial = parseSubsetDenial(error);
  if (denial) {
    return (
      <SubsetDeniedAlert
        denial={denial}
        title="Couldn't grant the requested permissions."
        intro="You don't hold these permissions yourself:"
        resolution="Remove the grant, or ask an administrator to grant them to you."
      />
    );
  }
  return (
    <div role="alert" className="rounded-md border-l-2 border-l-destructive bg-card p-4">
      <p className="text-sm font-medium">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">
        {agentErrorMessage(error, "We couldn't do this right now. Please try again.")}
      </p>
    </div>
  );
}

/**
 * Agent detail (design §19 — minimal T6 contract on the P1 detail pattern):
 * edit instructions/name + isActive (`PATCH /agent/factories/:id`), attach /
 * detach memory (`POST|DELETE …/memory`), attach/revoke policies
 * (`POST …/policies`, `DELETE …/policies/:policyId`), list/add/remove triggers
 * (`GET|POST …/triggers`, `DELETE …/triggers/:bindingId`) — the SAME endpoints
 * and the SAME subset-denied handling as the wizard (H3: one pattern
 * everywhere). No new backend surfaces.
 */
export function AgentDetailPage() {
  const { orgId = '', agentId = '' } = useParams<{ orgId: string; agentId: string }>();
  const toast = useToast();

  const factoryQuery = useGetFactoryQuery(agentId);
  const { data: modelCollections } = useGetModelCollectionsQuery();
  const triggersQuery = useGetTriggersQuery({ id: agentId });

  const [updateFactory] = useUpdateFactoryMutation();
  const [attachMemory] = useAttachMemoryMutation();
  const [detachMemory] = useDetachMemoryMutation();
  const [attachPolicies] = useAttachPoliciesMutation();
  const [revokePolicy] = useRevokePolicyMutation();
  const [createTrigger] = useCreateTriggerMutation();
  const [deleteTrigger] = useDeleteTriggerMutation();

  const factory = factoryQuery.data;
  const updateResource = `agent-factory:factory:${agentId}`;

  // — basics editor —
  const [name, setName] = useState('');
  const [instructions, setInstructions] = useState('');
  useEffect(() => {
    if (factory) {
      setName(factory.name);
      setInstructions(factory.instructions);
    }
  }, [factory]);
  const basicsDirty =
    Boolean(factory) && (name !== factory?.name || instructions !== factory?.instructions);
  const [savingBasics, setSavingBasics] = useState(false);

  const saveBasics = async () => {
    if (!factory || name.trim() === '') {
      return;
    }
    setSavingBasics(true);
    try {
      await updateFactory({ id: agentId, changes: { name: name.trim(), instructions } }).unwrap();
      toast.success('Agent updated.');
    } catch (error) {
      toast.error(agentErrorMessage(error, "Couldn't update the agent."));
    } finally {
      setSavingBasics(false);
    }
  };

  const toggleActive = async () => {
    if (!factory) {
      return;
    }
    try {
      await updateFactory({ id: agentId, changes: { isActive: !factory.isActive } }).unwrap();
      toast.success(factory.isActive ? 'Agent deactivated.' : 'Agent activated.');
    } catch (error) {
      toast.error(agentErrorMessage(error, "Couldn't update the agent."));
    }
  };

  // — memory —
  const [memoryBusy, setMemoryBusy] = useState(false);
  const [memoryError, setMemoryError] = useState<unknown>(undefined);
  const [detachOpen, setDetachOpen] = useState(false);
  const attachedCollections = memoryCollectionIds(factory);

  const runAttachMemory = async () => {
    setMemoryBusy(true);
    setMemoryError(undefined);
    try {
      await attachMemory({ id: agentId }).unwrap();
      toast.success('Memory attached.');
    } catch (error) {
      setMemoryError(error);
    } finally {
      setMemoryBusy(false);
    }
  };

  const runDetachMemory = async () => {
    setDetachOpen(false);
    setMemoryBusy(true);
    setMemoryError(undefined);
    try {
      await detachMemory({ id: agentId }).unwrap();
      toast.success('Memory detached.');
    } catch (error) {
      setMemoryError(error);
    } finally {
      setMemoryBusy(false);
    }
  };

  // — extra policies —
  const [statements, setStatements] = useState<GrantStatementDraft[]>([]);
  const [policyErrors, setPolicyErrors] = useState<StepErrors>({});
  const [policyBusy, setPolicyBusy] = useState(false);
  const [policyError, setPolicyError] = useState<unknown>(undefined);

  const runAttachPolicies = async () => {
    if (statements.length === 0) {
      return;
    }
    setPolicyBusy(true);
    setPolicyError(undefined);
    try {
      await attachPolicies({
        id: agentId,
        policySpecs: [toExtraGrantsSpec(statements)],
      }).unwrap();
      toast.success('Policies attached.');
      setStatements([]);
      setPolicyErrors({});
      void factoryQuery.refetch();
    } catch (error) {
      setPolicyError(error);
    } finally {
      setPolicyBusy(false);
    }
  };

  const runRevokePolicy = async (policyId: string) => {
    setPolicyError(undefined);
    try {
      await revokePolicy({ id: agentId, policyId }).unwrap();
      toast.success('Policy revoked.');
    } catch (error) {
      setPolicyError(error);
    }
  };

  // — triggers —
  const [pendingTriggers, setPendingTriggers] = useState<TriggerDraft[]>([]);
  const [triggerBusy, setTriggerBusy] = useState(false);
  const [triggerError, setTriggerError] = useState<unknown>(undefined);
  const [revealedWebhookUrls, setRevealedWebhookUrls] = useState<string[]>([]);

  const applyPendingTriggers = async () => {
    if (pendingTriggers.length === 0) {
      return;
    }
    setTriggerBusy(true);
    setTriggerError(undefined);
    try {
      for (const item of pendingTriggers) {
        const created = await createTrigger({ id: agentId, body: triggerBody(item) }).unwrap();
        if (created.webhookUrl) {
          setRevealedWebhookUrls((prev) => [...prev, created.webhookUrl as string]);
        }
      }
      toast.success('Triggers attached.');
      setPendingTriggers([]);
    } catch (error) {
      setTriggerError(error);
    } finally {
      setTriggerBusy(false);
    }
  };

  const runDeleteTrigger = async (bindingId: string) => {
    setTriggerError(undefined);
    try {
      await deleteTrigger({ id: agentId, bindingId }).unwrap();
      toast.success('Trigger removed.');
    } catch (error) {
      setTriggerError(error);
    }
  };

  const modelName = useMemo(
    () =>
      factory
        ? ((modelCollections ?? []).find((c) => c.id === factory.modelCollectionId)?.name ??
          factory.modelCollectionId)
        : undefined,
    [factory, modelCollections],
  );

  if (factoryQuery.isLoading) {
    return <LoadingRows label="Loading agent" columns={2} />;
  }
  if (factoryQuery.isError || !factory) {
    return (
      <QueryErrorView
        error={factoryQuery.error}
        label="the agent"
        onRetry={() => void factoryQuery.refetch()}
      />
    );
  }

  return (
    <section aria-labelledby="agent-detail-title" className="space-y-6">
      <Breadcrumb
        items={[
          { label: 'Organization', to: `/organizations/${orgId}` },
          { label: 'Agents', to: `/organizations/${orgId}/agents` },
          { label: factory.name },
        ]}
      />

      <PageHeader
        title={factory.name}
        description={modelName ? `Model: ${modelName}` : undefined}
        titleId="agent-detail-title"
        actions={
          <div className="flex items-center gap-2">
            <Badge variant={factory.isActive ? 'success' : 'secondary'}>
              {factory.isActive ? 'Active' : 'Inactive'}
            </Badge>
            <Can action={UPDATE_ACTION} resource={updateResource}>
              <Button variant="outline" size="sm" onClick={() => void toggleActive()}>
                {factory.isActive ? 'Deactivate' : 'Activate'}
              </Button>
            </Can>
          </div>
        }
      />

      {/* — basics — */}
      <div className="space-y-4 rounded-lg border bg-card p-4">
        <h2 className="text-lg font-semibold">Basics</h2>
        <div className="space-y-1.5">
          <Label htmlFor="detail-name">Name</Label>
          <Input id="detail-name" value={name} onChange={(event) => setName(event.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="detail-instructions">Instructions</Label>
          <Textarea
            id="detail-instructions"
            rows={8}
            className="min-h-40 resize-y"
            value={instructions}
            onChange={(event) => setInstructions(event.target.value)}
            aria-describedby="detail-instructions-helper"
          />
          <p id="detail-instructions-helper" className="text-sm text-muted-foreground">
            The system prompt your agent follows.
          </p>
        </div>
        <Can action={UPDATE_ACTION} resource={updateResource}>
          <div className="flex justify-end">
            <Button onClick={() => void saveBasics()} disabled={!basicsDirty || savingBasics}>
              {savingBasics ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </Can>
      </div>

      {/* — memory — */}
      <div className="space-y-4 rounded-lg border bg-card p-4">
        <h2 className="text-lg font-semibold">Memory</h2>
        {memoryError ? <AttachError error={memoryError} title="Couldn't update memory." /> : null}
        {attachedCollections.length > 0 ? (
          <>
            <GrantPanel />
            <div className="flex justify-end">
              <Can action={UPDATE_ACTION} resource={updateResource}>
                <Button variant="outline" onClick={() => setDetachOpen(true)} disabled={memoryBusy}>
                  Detach memory
                </Button>
              </Can>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              No memory collection attached. Attaching gives the agent a private collection it can
              read and write.
            </p>
            <Can action={UPDATE_ACTION} resource={updateResource}>
              <Button
                variant="secondary"
                onClick={() => void runAttachMemory()}
                disabled={memoryBusy}
              >
                {memoryBusy ? 'Attaching…' : 'Attach memory'}
              </Button>
            </Can>
          </div>
        )}
      </div>

      {/* — policies — */}
      <div className="space-y-4 rounded-lg border bg-card p-4">
        <h2 className="text-lg font-semibold">Policies</h2>
        {policyError ? <AttachError error={policyError} title="Couldn't update policies." /> : null}
        <div className="space-y-2">
          <p className="text-sm font-medium">Attached policies</p>
          {factory.policyIds.length === 0 ? (
            <p className="text-sm text-muted-foreground">None.</p>
          ) : (
            <ul className="flex flex-wrap gap-2">
              {factory.policyIds.map((policyId) => (
                <li key={policyId} className="flex items-center gap-1">
                  <MonoChip>{policyId}</MonoChip>
                  <Can action={UPDATE_ACTION} resource={updateResource}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      aria-label={`Revoke policy ${policyId}`}
                      onClick={() => void runRevokePolicy(policyId)}
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </Can>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="space-y-3">
          <p className="text-sm font-medium">Grant extra permissions</p>
          <p className="text-sm text-muted-foreground">
            You can only grant permissions you already hold.
          </p>
          <PolicyStatementBuilder
            statements={statements}
            onChange={setStatements}
            errors={policyErrors}
            onErrors={setPolicyErrors}
          />
          <Can action={UPDATE_ACTION} resource={updateResource}>
            <Button
              variant="secondary"
              onClick={() => void runAttachPolicies()}
              disabled={statements.length === 0 || policyBusy}
            >
              <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
              {policyBusy ? 'Attaching…' : 'Attach grants'}
            </Button>
          </Can>
        </div>
      </div>

      {/* — triggers — */}
      <div className="space-y-4 rounded-lg border bg-card p-4">
        <h2 className="text-lg font-semibold">Triggers</h2>
        {triggerError ? (
          <AttachError error={triggerError} title="Couldn't update triggers." />
        ) : null}
        <WebhookRevealPanel urls={revealedWebhookUrls} />

        <div className="space-y-2">
          <p className="text-sm font-medium">Active triggers</p>
          {triggersQuery.isLoading ? (
            <LoadingRows label="Loading triggers" />
          ) : (triggersQuery.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No triggers yet.</p>
          ) : (
            <ul className="space-y-2">
              {(triggersQuery.data ?? []).map((binding) => (
                <li
                  key={binding.id}
                  className="flex h-10 items-center gap-2 rounded-md border bg-card px-3"
                >
                  <Badge variant="secondary" className="rounded-sm">
                    {binding.kind === 'cron'
                      ? 'Cron'
                      : binding.kind === 'webhook'
                        ? 'Webhook'
                        : 'Event'}
                  </Badge>
                  <span className="min-w-0 flex-1 truncate font-mono text-xs">
                    {binding.kind === 'cron'
                      ? binding.cron
                      : binding.kind === 'event'
                        ? (binding.eventFilter ?? [])
                            .map((filter) => `${filter.channel}: ${filter.eventTypes.join(', ')}`)
                            .join(' · ')
                        : binding.webhookId}
                  </span>
                  <Can action={UPDATE_ACTION} resource={updateResource}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      aria-label={`Remove trigger ${binding.id}`}
                      onClick={() => void runDeleteTrigger(binding.id)}
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </Can>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-3">
          <p className="text-sm font-medium">Add triggers</p>
          <TriggerBuilder items={pendingTriggers} onChange={setPendingTriggers} />
          <Can action={UPDATE_ACTION} resource={updateResource}>
            <Button
              variant="secondary"
              onClick={() => void applyPendingTriggers()}
              disabled={pendingTriggers.length === 0 || triggerBusy}
            >
              {triggerBusy ? 'Attaching…' : 'Attach triggers'}
            </Button>
          </Can>
        </div>
      </div>

      <ConfirmDialog
        open={detachOpen}
        onOpenChange={setDetachOpen}
        title="Detach memory?"
        description="The agent loses read/write access to its memory collection. The collection itself is kept for audit."
        confirmLabel="Detach"
        variant="destructive"
        onConfirm={() => void runDetachMemory()}
      />
    </section>
  );
}
